import React from "react";
import { Add, BundlesEmptyBlur } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import { DashedBorder } from "@/components/ui/dashed-border";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import { cn } from "@/utils/utils";

interface ProductTabEmptyStateProps {
  /** The tab's hexagon badge, drawn larger than its hexagon by its shadow. */
  badge: React.ReactNode;
  /** Offsets that line the badge's hexagon up with the box it takes in layout. */
  badgeClassName: string;
  title: string;
  subtitle: string;
  actionLabel: string;
  onAction: () => void;
  isActionDisabled?: boolean;
  actionTooltip?: string;
  dataTest: string;
  actionDataTest: string;
}

export const ProductTabEmptyState: React.FC<ProductTabEmptyStateProps> = ({
  badge,
  badgeClassName,
  title,
  subtitle,
  actionLabel,
  onAction,
  isActionDisabled = false,
  actionTooltip,
  dataTest,
  actionDataTest
}) => (
  <DashedBorder
    className="flex w-full flex-col items-center justify-center gap-6 overflow-hidden rounded-2xl p-10"
    data-test={dataTest}
  >
    {/* Figma puts the same glow behind both tabs' empty states. */}
    <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
      <BundlesEmptyBlur />
    </div>
    {/* Only the hexagon takes up layout space; its shadow overflows. */}
    <div className="relative h-16 w-[59px] shrink-0">
      <div className={cn("absolute", badgeClassName)}>{badge}</div>
    </div>
    <div className="relative flex w-full flex-col items-center gap-2 text-center">
      <Text variant="body">{title}</Text>
      <Text variant="bodySecondary">{subtitle}</Text>
    </div>
    <Button
      className="relative w-fit"
      onClick={onAction}
      disabled={isActionDisabled}
      tooltipText={actionTooltip}
      data-test={actionDataTest}
    >
      <IconContainer as="span" size="18" color="current" aria-hidden="true">
        <Add />
      </IconContainer>
      {actionLabel}
    </Button>
  </DashedBorder>
);
